import React from 'react';
import { AlertCircle, Home, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { captureException } from '../lib/monitoring';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    // Forward to monitoring (Sentry). No-op if not initialized.
    captureException(error, {
      componentStack: errorInfo?.componentStack,
      source: 'ErrorBoundary',
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
            </div>

            <h1 className="text-2xl font-serif text-center text-slate-900 mb-2">
              Something went wrong
            </h1>

            <p className="text-center text-slate-600 mb-6">
              We encountered an unexpected error. Please try again or go back home.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 max-h-32 overflow-auto">
                <p className="text-xs font-mono text-red-700 break-words">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Button
                onClick={this.handleReset}
                className="w-full bg-accent hover:bg-accent/90 text-white rounded-xl h-11"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Try Again
              </Button>

              <a href="/" className="block">
                <Button
                  variant="outline"
                  className="w-full rounded-xl h-11"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </Button>
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
